import {
  List,
  Edit,
  Create,
  SimpleForm,
  TextField,
  DateField,
  UrlField,
  TextInput,
  DateInput,
  CreateButton,
  EditButton,
  DeleteButton,
  required,
  useTranslate,
  useListContext,
  Toolbar,
  SaveButton,
  useRecordContext,
  useGetList,
  Loading,
  Show,
  SimpleShowLayout,
  SelectField,
  useNotify,
  NumberInput,
  minValue,
  maxValue,
  ExportButton,
  FunctionField
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import downloadCSVCompat from '../../common_modules/utils/downloadCSVCompat';
import useErrorHandler from '../../common_modules/hooks/useErrorHandler';
import { getLocalStorageId } from '../../authProvider';
import { Box, Typography, InputAdornment, IconButton, useMediaQuery, useTheme, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FormValidation from '../../common_modules/components/FormValidation';
import PercentNumberField from '../../common_modules/components/PercentNumberField';
import PercentNumberInput from '../../common_modules/components/PercentNumberInput';
import FlexibleListLayout from '../../common_modules/components/FlexibleListLayout';
import Resizer from 'react-image-file-resizer';
import NumericRadioButtonGroupInput from '../../common_modules/components/NumericRadioButtonGroupInput';
import { format, toZonedTime } from 'date-fns-tz';
import { Link, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PersistentList } from '../../common_modules/components/PersistentList';

const jstTimeZone = 'Asia/Tokyo';

// ============================================================================
// 1. ファイル全体で使う定数とヘルパー関数
// ============================================================================
const statusChoices = [
  { id: 0, name: '無効' },
  { id: 1, name: '有効' }
];

const validateIsAfterToday = (value: string | Date): string | undefined => {
  if (!value) return undefined;
  const inputDate = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (inputDate < today) {
    return '本日以降の日付を指定してください。';
  }
  return undefined;
};

const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string | null> => {
  return new Promise((resolve) => {
    if (!file) return resolve(null);
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      'PNG',
      100,
      0,
      (uri) => {
        resolve(uri as string);
      },
      'base64'
    );
  });
};

const setStartOfDayJST = (date: Date | string): Date => {
  const zonedDate = toZonedTime(new Date(date), jstTimeZone);
  zonedDate.setHours(0, 0, 0, 0);
  return zonedDate;
};

const setEndOfDayJST = (date: Date | string): Date => {
  const zonedDate = toZonedTime(new Date(date), jstTimeZone);
  zonedDate.setHours(23, 59, 59, 999);
  return zonedDate;
};

const baseTransform = async (data: any) => {
  let transformedData = { ...data };
  if (transformedData.productImage && transformedData.productImage.rawFile) {
    transformedData.productImage = await resizeImage(transformedData.productImage.rawFile, 360, 300);
  } else if (transformedData.productImage && transformedData.productImage.src) {
    transformedData.productImage = transformedData.productImage.src;
  }

  if (transformedData.operationStartDate) {
    transformedData.operationStartDate = setStartOfDayJST(transformedData.operationStartDate).toISOString();
  }
  if (transformedData.operationEndDate) {
    transformedData.operationEndDate = setEndOfDayJST(transformedData.operationEndDate).toISOString();
  }
  if (transformedData.productReward !== null && transformedData.productReward !== undefined) {
    transformedData.productReward = String(transformedData.productReward);
  }

  return transformedData;
};

const JSTDateField = ({ source, label }: { source: string; label?: string }) => (
  <FunctionField
    label={label}
    render={(record: any) => {
      if (!record || !record[source]) return null;
      const date = new Date(record[source]);
      // date-fns-tz を使って JST としてフォーマット
      return format(toZonedTime(date, jstTimeZone), 'yyyy/MM/dd', { timeZone: jstTimeZone });
    }}
  />
);

// ============================================================================
// 2. UI用のヘルパーコンポーネント群
// ============================================================================
const ResetFormOnSuccessToolbar = ({ isExclude }: { isExclude?: boolean }) => {
  const notify = useNotify();
  const { reset, setError } = useFormContext();

  const onError = (error: any) => {
    if (error.body && error.body.errors) {
      if (isExclude) {
        // 【報酬除外商品フォームの処理】
        const uniqueMessages = Array.from(new Set(Object.values(error.body.errors) as string[]));
        const messages = uniqueMessages.join('. ');
        setError('productId', { type: 'manual', message: messages });
      } else {
        // 【キャンペーン商品フォームの処理】
        for (const [field, message] of Object.entries(error.body.errors)) {
          setError(field, { type: 'manual', message: message as string });
        }
      }
    } else {
      notify('ra.notification.error', { type: 'error', message: error.message || 'ra.message.error' });
    }
  };

  const handleSuccess = () => {
    reset();
    notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
  };

  return (
    <Toolbar>
      <SaveButton mutationOptions={{ onError, onSuccess: handleSuccess }} type="button" />
    </Toolbar>
  );
};

const CustomDeleteButton = ({ redirect = false }: { redirect?: string | boolean }) => {
  const record = useRecordContext();
  return (
    <Box sx={{ marginLeft: '16px' }}>
      <DeleteButton
        redirect={`/settings/${getLocalStorageId()}`}
        confirmTitle={`${record?.productId} の報酬設定を削除`}
        confirmContent="本当に削除しますか？"
        mutationMode="pessimistic"
      />
    </Box>
  );
};

const CustomEditToolbar = ({ isFormValid, isExclude }: { isFormValid: boolean; isExclude?: boolean }) => {
  const notify = useNotify();
  const { setError } = useFormContext();

  const onError = (error: any) => {
    if (error.body && error.body.errors) {
      if (isExclude) {
        const uniqueMessages = Array.from(new Set(Object.values(error.body.errors) as string[]));
        const messages = uniqueMessages.join('. ');
        setError('productId', { type: 'manual', message: messages });
      } else {
        for (const [field, message] of Object.entries(error.body.errors)) {
          setError(field, { type: 'manual', message: message as string });
        }
      }
    } else {
      notify('ra.notification.error', { type: 'error', message: error.message || 'ra.message.error' });
    }
  };

  return (
    <Toolbar sx={{ justifyContent: 'space-between', width: '100%', mt: 2 }}>
      <SaveButton disabled={!isFormValid} mutationOptions={{ onError }} />
      <CustomDeleteButton />
    </Toolbar>
  );
};

export const ListActions = ({ isExclude = false }: { isExclude?: boolean }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const createPath = isExclude ? '/product-reward-settings/create-exclusion' : '/product-reward-settings/create';
  const createLabel = isExclude
    ? translate('resources.product-reward-settings.actions.create-exclusion')
    : translate('resources.product-reward-settings.actions.create');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Typography variant="h6" component="h2">
        {isExclude
          ? translate('resources.product-reward-settings.fields.exclusion')
          : translate('resources.product-reward-settings.fields.campaign')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ExportButton label="CSVダウンロード" icon={<DownloadIcon sx={{ fontSize: '20px', marginRight: '4px' }} />} />
        {isSmall ? (
          <IconButton color="primary" aria-label={createLabel} component="a" href={`#${createPath}`} size="medium">
            <AddIcon />
          </IconButton>
        ) : (
          <Button variant="text" color="primary" startIcon={<AddIcon />} component="a" href={`#${createPath}`}>
            {createLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// 3. リスト表示用のコンポーネント
// ============================================================================
const WrappingUrlField = ({ source, label, sx }: { source: string; label?: string; sx?: object }) => (
  <FunctionField
    label={label}
    render={(record: any) =>
      record && record[source] ? (
        <Tooltip title={record[source]}>
          <Link
            href={record[source]}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            sx={sx} // 親から渡されたsxスタイルを適用
          >
            {record[source]}
          </Link>
        </Tooltip>
      ) : null
    }
  />
);

const CustomListLayout = ({ title = '', isExclude = false }: { title?: string; isExclude?: boolean }) => {
  const { total, isLoading } = useListContext();
  const translate = useTranslate();
  if (isLoading) return null;
  const wrappingTextStyle = {
    whiteSpace: 'normal',
    wordBreak: 'break-all'
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ pl: 2 }}>
        {title}
      </Typography>
      {total > 0 && (
        <FlexibleListLayout
          rowClick="show"
          bulkActionButtons={false}
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& th': {
              // th (ヘッダーセル) のみに適用
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            },
            '& td': {
              // td (データセル) は overflow のみ
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        >
          <TextField
            source="productName"
            label={translate(
              isExclude
                ? 'resources.product-reward-settings.fields.productNameExclusion'
                : 'resources.product-reward-settings.fields.productName'
            )}
            sx={wrappingTextStyle}
          />
          <TextField
            source="productId"
            label={translate('resources.product-reward-settings.fields.productId')}
            sx={wrappingTextStyle}
          />
          <PercentNumberField
            source="productReward"
            label={translate(
              isExclude
                ? 'resources.product-reward-settings.fields.productRewardExclusion'
                : 'resources.product-reward-settings.fields.productReward'
            )}
          />
          {!isExclude && (
            <JSTDateField
              source="operationStartDate"
              label={translate('resources.product-reward-settings.fields.operationStartDate')}
            />
          )}
          {!isExclude && (
            <JSTDateField
              source="operationEndDate"
              label={translate('resources.product-reward-settings.fields.operationEndDate')}
            />
          )}
          {!isExclude && (
            <WrappingUrlField
              source="productUrl"
              label={translate('resources.product-reward-settings.fields.productUrl')}
              sx={wrappingTextStyle}
            />
          )}{' '}
          {!isExclude && (
            <SelectField
              source="invalidFlag"
              label={translate('resources.product-reward-settings.fields.invalidFlag')}
              choices={statusChoices}
            />
          )}
          <FunctionField
            label=" "
            render={() => (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <EditButton />
                <CustomDeleteButton />
              </Box>
            )}
          />
        </FlexibleListLayout>
      )}
      {total === 0 && (
        <Box textAlign="center" m={2}>
          <Typography variant="body2">{translate('ra.navigation.no_results')}</Typography>
        </Box>
      )}
    </>
  );
};

export const ProductRewardList = () => {
  const translate = useTranslate();

  const csvExporter = (isExclude: boolean) => (data: any[]) => {
    if (!data || data.length === 0) {
      return;
    }
    const dataForExport = data.map((record) => {
      const status = statusChoices.find((c) => c.id === record.invalidFlag)?.name ?? record.invalidFlag;

      // isExcludeフラグに応じて出力するカラムを変更
      if (isExclude) {
        return {
          [translate('resources.product-reward-settings.fields.productNameExclusion')]: record.productName,
          [translate('resources.product-reward-settings.fields.productId')]: record.productId,
          [translate('resources.product-reward-settings.fields.productRewardExclusion')]: record.productReward
        };
      } else {
        return {
          [translate('resources.product-reward-settings.fields.productName')]: record.productName,
          [translate('resources.product-reward-settings.fields.productId')]: record.productId,
          [translate('resources.product-reward-settings.fields.productReward')]: record.productReward,
          [translate('resources.product-reward-settings.fields.operationStartDate')]: format(
            toZonedTime(new Date(record.operationStartDate), jstTimeZone),
            'yyyy/MM/dd',
            { timeZone: jstTimeZone }
          ),
          [translate('resources.product-reward-settings.fields.operationEndDate')]: format(
            toZonedTime(new Date(record.operationEndDate), jstTimeZone),
            'yyyy/MM/dd',
            { timeZone: jstTimeZone }
          ),
          [translate('resources.product-reward-settings.fields.productUrl')]: record.productUrl,
          [translate('resources.product-reward-settings.fields.invalidFlag')]: status
        };
      }
    });
    const filename = isExclude
      ? translate('resources.product-reward-settings.fields.exclusion')
      : translate('resources.product-reward-settings.fields.productName');
    jsonExport(dataForExport, (err, csv) => {
      if (err) {
        console.error(err);
        return;
      }
      const bom = '\uFEFF';
      downloadCSVCompat(bom + csv, filename);
    });
  };

  const { isLoading: isZeroRewardLoading } = useGetList('product-reward-settings', {
    filter: { productReward: 0 }
  });

  if (isZeroRewardLoading) return <Loading />;

  return (
    <>
      <PersistentList
        title=" "
        resource="product-reward-settings"
        storeKey="campaign-products"
        actions={<ListActions />}
        filter={{ productReward: { not: 0 } }}
        sx={{ mt: 2 }}
        empty={false}
        exporter={csvExporter(false)}
      >
        <CustomListLayout />
      </PersistentList>

      <PersistentList
        title=" "
        resource="product-reward-settings"
        storeKey="excluded-products"
        actions={<ListActions isExclude={true} />}
        filter={{ productReward: 0 }}
        sx={{ mt: 4 }}
        empty={false}
        exporter={csvExporter(true)}
      >
        <CustomListLayout isExclude={true} />
      </PersistentList>
    </>
  );
};

// ============================================================================
// 4. 作成・編集フォーム用のコンポーネント
// ============================================================================
const ProductRewardFormFields = ({ isEdit, isExclude = false }: { isEdit: boolean; isExclude?: boolean }) => {
  const { watch, setValue, getValues } = useFormContext();
  const translate = useTranslate();
  const productRewardValue = watch('productReward');
  const isExclusionProduct = isEdit ? productRewardValue == 0 : isExclude;
  const wrappingTextStyle = {
    whiteSpace: 'normal',
    wordBreak: 'break-all'
  };
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = event.target.value;
    if (!dateString) return; // 日付がクリアされた場合は何もしない

    const date = new Date(dateString);
    const endDate = getValues('operationEndDate');

    // 新規作成時で、かつ終了日がまだ設定されていない場合のみ動作
    if (!isEdit && !endDate && date) {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      setValue('operationEndDate', nextDay, { shouldValidate: true });
    }
  };

  const formatDateForInput = (date: Date) => {
    if (!date || !(date instanceof Date)) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <>
      <TextInput
        source="productName"
        label={translate(
          isExclusionProduct
            ? 'resources.product-reward-settings.fields.productNameExclusion'
            : 'resources.product-reward-settings.fields.productName'
        )}
        validate={required()}
        fullWidth
        sx={wrappingTextStyle}
      />
      <TextInput
        source="productId"
        validate={required()}
        sx={wrappingTextStyle}
        format={(value) => (value ? value.replace(/\s/g, '') : '')}
        parse={(value) => (value ? value.replace(/\s/g, '') : '')}
      />
      <PercentNumberInput
        source="productReward"
        label={translate(
          isExclusionProduct
            ? 'resources.product-reward-settings.fields.productRewardExclusion'
            : 'resources.product-reward-settings.fields.productReward'
        )}
        validate={!isExclude ? [required(), minValue(0.01), maxValue(99.99)] : []}
        min={!isExclude ? 0.01 : undefined}
        max={99.99}
        readOnly={isExclusionProduct}
        defaultValue={isExclusionProduct ? 0 : undefined}
      />
      {!isExclude && (
        <>
          <DateInput
            source="operationStartDate"
            defaultValue={!isEdit ? new Date() : undefined}
            validate={!isExclusionProduct ? [required()] : undefined}
            onChange={handleStartDateChange}
          />
          <DateInput
            source="operationEndDate"
            defaultValue={!isEdit ? undefined : undefined}
            validate={!isExclusionProduct ? [required()] : undefined}
          />
          <TextInput source="productUrl" validate={required()} fullWidth sx={wrappingTextStyle} />
          <NumericRadioButtonGroupInput source="invalidFlag" choices={statusChoices} defaultValue={1} />
        </>
      )}
    </>
  );
};

const ProductRewardForm = ({ isExclude = false }: { isExclude?: boolean }) => {
  const record = useRecordContext();
  const isEdit = !!record?.id;

  const requiredFieldsForEdit = ['productId'];
  if (!isExclude) {
    requiredFieldsForEdit.push('productReward', 'operationStartDate', 'operationEndDate');
  }
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const defaultValuesForCreate = {
    operationStartDate: today,
    operationEndDate: tomorrow,
    invalidFlag: 1
  };

  return (
    <SimpleForm
      reValidateMode="onSubmit"
      defaultValues={!isEdit ? defaultValuesForCreate : undefined}
      toolbar={
        isEdit ? (
          <FormValidation requiredFields={requiredFieldsForEdit}>
            {(isValid) => <CustomEditToolbar isFormValid={isValid} isExclude={isExclude} />}
          </FormValidation>
        ) : (
          <ResetFormOnSuccessToolbar isExclude={isExclude} />
        )
      }
      sx={{ maxWidth: '600px' }}
    >
      <ProductRewardFormFields isEdit={isEdit} isExclude={isExclude} />
    </SimpleForm>
  );
};

export const ProductRewardCreate = ({ isExclude = false }: { isExclude?: boolean }) => {
  const handleError = useErrorHandler();
  const translate = useTranslate();
  const transform = async (data: any) => {
    const transformedData = await baseTransform(data);
    const clientId = parseInt(getLocalStorageId()!, 10);

    if (isExclude) {
      return {
        ...transformedData,
        productReward: '0',
        clientId: clientId,
        operationStartDate: setStartOfDayJST(new Date()),
        operationEndDate: setEndOfDayJST(new Date('9999-12-31')),
        invalidFlag: 1
      };
    }
    return { ...transformedData, clientId: clientId };
  };

  const listTitle = isExclude ? '既存の報酬除外商品' : '既存のキャンペーン対象商品';
  const listFilter = isExclude ? { productReward: 0 } : { productReward: { not: 0 } };

  return (
    <Create
      title={
        isExclude
          ? translate('resources.product-reward-settings.actions.create-exclusion')
          : translate('resources.product-reward-settings.actions.create')
      }
      transform={transform}
      mutationOptions={{ onError: handleError }}
      resource="product-reward-settings"
    >
      <ProductRewardForm isExclude={isExclude} />
      {/* 作成意図が不明のため念の為にコメントアウト */}
      {/* <Box sx={{ mt: 4, pb: 4 }}>
        <List storeKey={false} actions={false} title=" " filter={listFilter} exporter={false}>
          <CustomListLayout title={listTitle} isExclude={isExclude} />
        </List>
      </Box> */}
    </Create>
  );
};

const EditFormWrapper = () => {
  const record = useRecordContext();
  if (!record) return null;
  const isExclusion = record.productReward == 0;
  return <ProductRewardForm isExclude={isExclusion} />;
};

const EditPageLayout = () => {
  const record = useRecordContext();
  if (!record) return null;

  const isExclusion = record.productReward == 0;
  const listTitle = isExclusion ? '既存の報酬除外商品' : '既存のキャンペーン対象商品';
  const listFilter = isExclusion
    ? { productReward: 0, id: { not: record.id } }
    : {
        id: { not: record.id },
        productReward: { not: 0 }
      };

  return (
    <>
      <EditFormWrapper />
      {/* 作成意図が不明のため念の為にコメントアウト */}
      {/* <Box sx={{ mt: 4, pb: 4 }}>
        <List storeKey={false} actions={false} title=" " filter={listFilter} exporter={false}>
          <CustomListLayout title={listTitle} isExclude={isExclusion} />
        </List>
      </Box> */}
    </>
  );
};

export const ProductRewardEdit = () => {
  const handleError = useErrorHandler();
  const dynamicRedirect = (resource: string | undefined, id: any, data: any) => {
    return data.productReward == 0 ? 'product-reward-settings/create-exclusion' : 'product-reward-settings/create';
  };

  return (
    <Edit
      title={<ProductRewardShowTitle />}
      redirect={false}
      mutationMode="pessimistic"
      transform={baseTransform}
      mutationOptions={{ onError: handleError }}
    >
      <EditPageLayout />
    </Edit>
  );
};

// ============================================================================
// 5. 詳細表示コンポーネント
// ============================================================================

const ProductRewardShowLayout = () => {
  const record = useRecordContext();
  if (!record) {
    return <Loading />;
  }
  const wrappingTextStyle = {
    whiteSpace: 'normal',
    wordBreak: 'break-all'
  };
  const translate = useTranslate();
  const isExclusion = record.productReward == 0;
  return (
    <SimpleShowLayout record={record}>
      <TextField
        source="productName"
        label={translate(
          isExclusion
            ? 'resources.product-reward-settings.fields.productNameExclusion'
            : 'resources.product-reward-settings.fields.productName'
        )}
        sx={wrappingTextStyle}
      />
      <TextField source="productId" sx={wrappingTextStyle} />
      <PercentNumberField
        source="productReward"
        label={translate(
          isExclusion
            ? 'resources.product-reward-settings.fields.productRewardExclusion'
            : 'resources.product-reward-settings.fields.productReward'
        )}
      />
      {!isExclusion && <JSTDateField source="operationStartDate" />}
      {!isExclusion && <JSTDateField source="operationEndDate" />}
      {!isExclusion && <WrappingUrlField source="productUrl" sx={wrappingTextStyle} />}
      {!isExclusion && <SelectField source="invalidFlag" choices={statusChoices} />}
    </SimpleShowLayout>
  );
};

const ProductRewardShowTitle = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const isExclusion = record.productReward == 0;
  const resourceType = isExclusion
    ? translate('resources.product-reward-settings.fields.exclusion')
    : translate('resources.product-reward-settings.fields.campaign');

  // 商品名がある場合は商品名を、なければ空白
  const fullTitle = `${resourceType} #${record.productName || ''}`;

  return (
    <Typography
      component="span"
      variant="h6"
      sx={{
        display: 'inline-block',
        maxWidth: '65vw',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
        minWidth: 0
      }}
    >
      {fullTitle}
    </Typography>
  );
};

export const ProductRewardShow = () => (
  <Show title={<ProductRewardShowTitle />}>
    <ProductRewardShowLayout />
  </Show>
);
